<template>
	<v-container fluid class = "pa-0 access-control-multi-face-roster">
		<v-card outlined class = "access-control-multi-face-card">
			<v-card-title class = "pa-1 px-2 access-control-multi-face-header vue-draggable-handle">
				<div class = "access-control-multi-face-header-icon">
					<v-icon small color = "primary">mdi-account-group</v-icon>
				</div>
				<div class = "access-control-multi-face-header-copy">
					<div class = "access-control-multi-face-title">{{ mlText('multi_face_roster') }}</div>
					<div class = "access-control-multi-face-subtitle">{{ mlText('multi_face_roster_subtitle') }}</div>
				</div>
				<v-spacer></v-spacer>
				<v-btn
					x-small
					color = "primary"
					class = "mr-1"
					:disabled = "actionLoading"
					@click = "openAddPersonDialog"
				>
					<v-icon x-small left>mdi-account-plus</v-icon>
					{{ mlText('multi_face_roster_add_person') }}
				</v-btn>
				<v-chip x-small outlined color = "primary" class = "mr-1">{{ terminalCountLabel }}</v-chip>
				<v-btn
					icon
					x-small
					:loading = "loading"
					:aria-label = "mlText('multi_face_roster_refresh')"
					:title = "mlText('multi_face_roster_refresh')"
					class = "access-control-multi-face-refresh"
					@click = "loadRosters"
				>
					<v-icon x-small>mdi-refresh</v-icon>
				</v-btn>
			</v-card-title>

			<v-card-text class = "pa-1 access-control-multi-face-body">
				<div class = "access-control-multi-face-toolbar">
					<div class = "access-control-multi-face-summary">
						<div class = "access-control-multi-face-count">{{ peopleCountLabel(filteredPeople.length) }}</div>
						<div class = "caption text--secondary">{{ summaryCountLabel }}</div>
					</div>
					<div v-if = "hasSelectablePeople" class = "access-control-multi-face-selection-actions">
						<div class = "caption font-weight-medium">{{ selectedCountLabel }}</div>
						<v-btn text x-small :disabled = "!selectedCount || importLoading" @click = "clearSelection">{{ mlText('multi_face_roster_clear_selection') }}</v-btn>
						<v-btn color = "primary" x-small :disabled = "!selectedCount" :loading = "importLoading" @click = "openImportDialog">{{ mlText('multi_face_roster_import_to_list') }}</v-btn>
					</div>
					<v-select
						v-model = "groupMode"
						:items = "groupOptions"
						dense
						outlined
						hide-details
						:label = "mlText('multi_face_roster_group_label')"
						class = "access-control-multi-face-group"
					></v-select>
					<v-text-field
						v-model = "filterQuery"
						dense
						outlined
						clearable
						hide-details
						prepend-inner-icon = "mdi-magnify"
						:label = "mlText('multi_face_roster_search_label')"
						class = "access-control-multi-face-filter"
					></v-text-field>
				</div>

				<div v-if = "deviceStates.length" class = "access-control-multi-face-states">
					<v-chip
						v-for = "state in deviceStates"
						:key = "state.id"
						x-small
						outlined
						:color = "deviceStateColor(state)"
						:title = "deviceStateTitle(state)"
						class = "mr-1 mb-1"
					>
						{{ state.name }} · {{ deviceStateText(state) }}
					</v-chip>
				</div>

				<v-alert v-if = "partialErrorText" dense outlined type = "warning" class = "mb-2 access-control-multi-face-note">
					{{ partialErrorText }}
				</v-alert>
				<v-alert v-if = "importSuccess" dense outlined type = "success" class = "mb-2 access-control-multi-face-note">
					{{ importSuccess }}
				</v-alert>
				<v-alert v-if = "importError" dense outlined type = "warning" class = "mb-2 access-control-multi-face-note">
					{{ importError }}
				</v-alert>
				<v-alert v-if = "actionSuccess" dense outlined type = "success" class = "mb-2 access-control-multi-face-note">
					{{ actionSuccess }}
				</v-alert>
				<v-alert v-if = "actionError" dense outlined type = "warning" class = "mb-2 access-control-multi-face-note">
					{{ actionError }}
				</v-alert>

				<div v-if = "loading && !people.length" class = "text-center pa-4 text--secondary">
					<v-progress-circular indeterminate size = "22" width = "2" color = "primary"></v-progress-circular>
					<div class = "caption mt-2">{{ mlText('multi_face_roster_loading_people') }}</div>
				</div>

				<v-alert v-else-if = "loadError && !people.length" dense outlined type = "warning" class = "mb-0">
					{{ loadError }}
				</v-alert>

				<div v-else-if = "!groupedSections.length" class = "access-control-multi-face-empty">
					<v-icon size = "32">mdi-account-search</v-icon>
					<div class = "font-weight-medium mt-2">{{ mlText('multi_face_roster_empty_title') }}</div>
					<div class = "caption text--secondary">{{ mlText('multi_face_roster_empty_filter') }}</div>
				</div>

				<div v-else class = "access-control-multi-face-list">
								<v-card
						v-for = "section in groupedSections"
						:key = "section.key"
						outlined
						class = "access-control-multi-face-section mb-2"
					>
						<div class = "access-control-multi-face-section-header">
							<div>
								<div class = "font-weight-bold">{{ section.title }} ({{ section.people.length }})</div>
								<div class = "caption text--secondary">{{ section.subtitle }}</div>
							</div>
							<v-chip x-small outlined color = "primary">{{ section.people.length }}</v-chip>
						</div>

						<div class = "access-control-multi-face-grid">
							<v-card
								v-for = "person in section.people"
								:key = "`${section.key}-${person.key}`"
								outlined
								:class = "['access-control-multi-face-person-card', { 'access-control-multi-face-person-card--clickable': canOpenPersonDetail(person), 'access-control-multi-face-person-card--selectable': isSelectablePerson(person), 'access-control-multi-face-person-card--selected': isSelected(person) }]"
								@click = "openPersonDetail(person)"
							>
								<v-btn
									v-if = "isSelectablePerson(person)"
									icon
									x-small
									class = "access-control-multi-face-select-toggle"
									:aria-label = "selectionLabel(person)"
									:title = "selectionLabel(person)"
									@click.stop = "toggleSelection(person)"
								>
									<v-icon x-small>{{ isSelected(person) ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle-outline' }}</v-icon>
								</v-btn>
								<div class = "access-control-multi-face-photo-wrap">
									<img
										v-if = "personImageSource(person)"
										:src = "personImageSource(person)"
									:alt = "personPhotoAlt(person)"
										class = "access-control-multi-face-photo"
										@error = "markImageUnavailable(person)"
									>
									<v-avatar v-else size = "42" class = "access-control-multi-face-avatar">
										<v-icon>mdi-account</v-icon>
									</v-avatar>
								</div>

								<div class = "access-control-multi-face-person-info">
									<div class = "font-weight-medium access-control-multi-face-person-name">{{ person.name || mlText('multi_face_roster_unnamed_person') }}</div>
									<div class = "caption text--secondary">FPID {{ person.FPID || '—' }}</div>
									<div class = "access-control-multi-face-terminals mt-1">
										<v-chip
											v-for = "terminal in person.terminals"
											:key = "`${person.key}-${terminal.id}`"
											x-small
											label
											outlined
											color = "primary"
											class = "access-control-multi-face-terminal-chip mr-1 mb-1"
										>
											<v-icon x-small left>mdi-door-open</v-icon>
											{{ terminal.name }}
										</v-chip>
									</div>
								</div>

								<div class = "access-control-multi-face-status">
									<v-chip x-small outlined :color = "statusColor(person)">{{ statusText(person) }}</v-chip>
									<div class = "caption text--secondary mt-1">{{ mlText('multi_face_roster_system') }}: {{ yesNoText(person.inSystem) }}</div>
									<div class = "caption text--secondary">{{ mlText('multi_face_roster_terminal') }}: {{ yesNoText(person.inTerminal) }}</div>
								</div>
							</v-card>
						</div>
					</v-card>
				</div>
			</v-card-text>
		</v-card>

		<v-dialog v-model = "importDialog" max-width = "560">
			<v-card class = "access-control-multi-face-import-card">
				<v-card-title class = "access-control-multi-face-import-header">
					<div>
						<div class = "access-control-multi-face-import-title">{{ mlText('multi_face_roster_import_people_title') }}</div>
						<div class = "caption text--secondary">{{ mlText('multi_face_roster_import_people_subtitle') }}</div>
					</div>
					<v-spacer></v-spacer>
					<v-btn icon small :aria-label = "mlText('multi_face_roster_close_import')" :title = "mlText('multi_face_roster_close_import')" @click = "closeImportDialog">
						<v-icon small>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text>
					<div class = "caption mb-2">{{ selectedReadyCountLabel }}</div>
					<v-select
						v-model = "selectedListId"
						:items = "faceListOptions"
						:loading = "listsLoading"
						item-text = "text"
						item-value = "value"
						dense
						outlined
						hide-details = "auto"
						:label = "mlText('multi_face_roster_face_list_label')"
						:no-data-text = "mlText('multi_face_roster_no_face_lists')"
					></v-select>

					<div v-if = "ambiguousSelectedPeople.length" class = "access-control-multi-face-source-list mt-3">
						<div class = "caption font-weight-medium mb-2">{{ mlText('multi_face_roster_source_required') }}</div>
						<div
							v-for = "person in ambiguousSelectedPeople"
							:key = "`source-${person.key}`"
							class = "access-control-multi-face-source-row"
						>
							<div class = "caption text--secondary">{{ person.name || mlText('multi_face_roster_unnamed_person') }} · FPID {{ person.FPID || '—' }}</div>
							<v-select
								:value = "sourceSelections[person.key] || ''"
								:items = "sourceOptions(person)"
								item-text = "text"
								item-value = "value"
								dense
								outlined
								hide-details = "auto"
								:label = "mlText('multi_face_roster_source_terminal_label')"
								@input = "setSourceSelection(person, $event)"
							></v-select>
						</div>
					</div>

					<v-alert v-if = "importError" dense outlined type = "warning" class = "mt-3 mb-0">{{ importError }}</v-alert>
				</v-card-text>
				<v-card-actions class = "px-4 pb-4">
					<v-spacer></v-spacer>
					<v-btn text small :disabled = "importLoading" @click = "closeImportDialog">{{ mlText('multi_face_roster_cancel') }}</v-btn>
					<v-btn color = "primary" small :disabled = "!canConfirmImport" :loading = "importLoading" @click = "confirmImport">{{ mlText('multi_face_roster_import') }}</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model = "personDetailDialog" max-width = "760" @input = "onPersonDetailDialogInput">
			<v-card v-if = "selectedPerson" class = "access-control-multi-face-detail-card">
				<v-card-title class = "access-control-multi-face-detail-header">
					<div>
						<div class = "access-control-multi-face-detail-title">{{ mlText('multi_face_roster_detail_title') }}</div>
						<div class = "caption text--secondary">{{ selectedPerson.name || mlText('multi_face_roster_unnamed_person') }}</div>
					</div>
					<v-spacer></v-spacer>
					<v-btn icon small :aria-label = "mlText('multi_face_roster_close_detail')" :title = "mlText('multi_face_roster_close_detail')" @click = "closePersonDetail">
						<v-icon small>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text>
					<v-alert v-if = "actionSuccess" dense outlined type = "success" class = "mb-3 access-control-multi-face-note">
						{{ actionSuccess }}
					</v-alert>
					<v-alert v-if = "actionError" dense outlined type = "warning" class = "mb-3 access-control-multi-face-note">
						{{ actionError }}
					</v-alert>
					<div class = "access-control-multi-face-detail-layout">
						<div class = "access-control-multi-face-detail-photo-wrap">
							<img
								v-if = "selectedPersonImageSource"
								:src = "selectedPersonImageSource"
								:alt = "personPhotoAlt(selectedPerson)"
								class = "access-control-multi-face-detail-photo"
							>
							<v-avatar v-else size = "150" class = "access-control-multi-face-avatar">
								<v-icon size = "54">mdi-account</v-icon>
							</v-avatar>
						</div>
					<div class = "access-control-multi-face-detail-info">
							<div class = "overline">{{ mlText('multi_face_roster_user_info') }}</div>
							<div class = "access-control-multi-face-detail-row"><strong>{{ mlText('multi_face_roster_name') }}:</strong> {{ selectedPerson.name || mlText('multi_face_roster_unnamed_person') }}</div>
							<div class = "access-control-multi-face-detail-row"><strong>FPID:</strong> {{ selectedPerson.FPID || '—' }}</div>
							<div class = "access-control-multi-face-detail-row"><strong>{{ mlText('multi_face_roster_status_summary') }}:</strong> {{ statusSummaryText(selectedPerson) }}</div>
							<div class = "access-control-multi-face-detail-terminals mt-2">
								<div class = "caption font-weight-medium mb-1">{{ mlText('multi_face_roster_present_terminals') }}</div>
								<div
									v-for = "terminal in selectedPerson.terminals"
									:key = "`detail-${selectedPerson.key}-${terminal.id}`"
									class = "access-control-multi-face-terminal-action-row"
								>
									<v-chip small label outlined color = "primary" class = "mr-1">
										<v-icon small left>mdi-door-open</v-icon>
										{{ terminal.name }}
									</v-chip>
									<v-btn x-small text color = "error" :disabled = "actionLoading" @click.stop = "confirmRemoveFromTerminal(selectedPerson, terminal)">{{ mlText('multi_face_roster_remove_from_terminal') }}</v-btn>
								</div>
							</div>
							<div class = "access-control-multi-face-detail-actions mt-3">
								<v-select
									v-model = "moveTargetTerminalIds"
									:items = "selectedPersonMoveTargetOptions"
									item-text = "text"
									item-value = "value"
									multiple
									chips
									small-chips
									dense
									outlined
									hide-details
									:label = "selectedPersonMoveTargetLabel"
									:no-data-text = "selectedPersonMoveNoDestinationsText"
									class = "access-control-multi-face-move-select"
								></v-select>
								<v-alert v-if = "!selectedPersonMoveTargetOptions.length" dense outlined type = "info" class = "mb-0 access-control-multi-face-note">
									{{ selectedPersonMoveNoDestinationsText }}
								</v-alert>
								<v-btn
									v-if = "isRegistered(selectedPerson)"
									x-small
									color = "primary"
									:disabled = "!canConfirmMovePerson"
									@click.stop = "confirmCopyPersonToTerminals(selectedPerson)"
								>{{ mlText('multi_face_roster_copy_to_terminal') }}</v-btn>
								<v-btn x-small color = "primary" :outlined = "isRegistered(selectedPerson)" :disabled = "!canConfirmMovePerson" @click.stop = "confirmSelectedPersonTerminalAction(selectedPerson)">{{ selectedPersonTerminalActionLabel }}</v-btn>
								<v-btn x-small outlined color = "error" :disabled = "actionLoading" @click.stop = "confirmDeleteCompletely(selectedPerson)">{{ mlText('multi_face_roster_delete_completely') }}</v-btn>
							</div>
						</div>
					</div>

					<v-divider class = "my-3"></v-divider>
					<div class = "access-control-multi-face-events-header">
						<div>
							<div class = "font-weight-bold">{{ mlText('multi_face_roster_events_chart_title') }}</div>
							<div class = "caption text--secondary">{{ mlText('multi_face_roster_events_chart_subtitle') }}</div>
						</div>
						<v-chip small outlined color = "primary">{{ formatText('multi_face_roster_events_total', [personEventTotal]) }}</v-chip>
					</div>
					<div class = "access-control-multi-face-chart-wrap mt-2">
						<div v-if = "personEventsLoading" class = "text-center pa-4 text--secondary">
							<v-progress-circular indeterminate size = "22" width = "2" color = "primary"></v-progress-circular>
							<div class = "caption mt-2">{{ mlText('multi_face_roster_events_loading') }}</div>
						</div>
						<v-alert v-else-if = "personEventsError" dense outlined type = "warning" class = "mb-0">{{ personEventsError }}</v-alert>
						<div v-else-if = "!hasPersonEventCounts" class = "access-control-multi-face-events-empty">
							<v-icon size = "28">mdi-chart-line</v-icon>
							<div class = "caption mt-2">{{ mlText('multi_face_roster_events_empty') }}</div>
						</div>
						<canvas v-else ref = "personEventsChart" class = "access-control-multi-face-events-chart"></canvas>
					</div>
				</v-card-text>
			</v-card>
		</v-dialog>

		<TrainedFaceEnrollmentModal
			v-model = "addPersonDialog"
			:initial-list-ids = "addPersonListIds"
			@saved = "handlePersonAdded"
		/>

		<v-dialog v-model = "confirmDialog" max-width = "520">
			<v-card>
				<v-card-title class = "body-1 font-weight-bold">{{ confirmTitle }}</v-card-title>
				<v-card-text>{{ confirmMessage }}</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text small :disabled = "actionLoading" @click = "closeConfirmDialog">{{ mlText('multi_face_roster_cancel') }}</v-btn>
					<v-btn color = "error" small :loading = "actionLoading" @click = "runConfirmedAction">{{ confirmLabel }}</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
	import Chart from 'chart.js'
	import AccessControlService from '@/services/accessControl.service.js'
	import FaceService from '@/services/face.service.js'
	import ListService from '@/services/list.service.js'
	import TrainedFaceEnrollmentModal from '@/components/face/TrainedFaceEnrollmentModal.vue'

	export const MULTI_FACE_ROSTER_LIMIT = 100
	export const MULTI_FACE_ROSTER_DEVICE_LIST_LIMIT = 200
	export const MULTI_FACE_ROSTER_EVENT_DAYS = 30
	export const MULTI_FACE_ROSTER_EVENT_LIMIT = 1000

	const FACE_TERMINAL_KEYWORDS = ['face', 'facial', 'rostro', 'biometric', 'biometrico', 'biométrico', 'terminal_hikvision']
	const ACCESS_TERMINAL_KEYWORDS = ['terminal', 'access', 'acceso', 'access-control', 'control_access', 'control_acceso']
	const KNOWN_DAHUA_FACE_TERMINAL_IDS = ['bk0257apaj5d82c']
	const DAHUA_FACE_TERMINAL_MODEL_PATTERNS = [/^asi6214(?:[a-z0-9-]*)?$/]
	const DAHUA_PREFIXED_FACE_TERMINAL_MODEL_PATTERNS = [/^dhi-asi6214(?:[a-z0-9-]*)?$/]

	const normalizedIdentifierFields = (item = {}) => [item.deviceId, item.device_id, item.serial, item.id]
		.filter(value => value !== undefined && value !== null)
		.map(value => String(value).trim().toLowerCase())
	const normalizedTextFields = (item = {}) => [item.vendor, item.brand, item.model, item.name, item.type_device, item.deviceType, item.terminalType, item.type_terminal]
		.filter(value => value !== undefined && value !== null)
		.map(value => String(value).toLowerCase())
	const normalizedDahuaEvidenceFields = (item = {}) => [
		item.model,
		item.type_device,
		item.deviceType,
		item.terminalType,
		item.type_terminal,
		item.metadata,
		item.meta,
	]
		.filter(value => value !== undefined && value !== null)
		.map(value => typeof value === 'object' ? JSON.stringify(value) : String(value))
		.map(value => value.toLowerCase())

	const hasKeyword = (fields, keywords) => fields.some(value => keywords.some(keyword => value.includes(keyword)))
	const normalizedDahuaModel = (item = {}) => item.model === undefined || item.model === null ? '' : String(item.model).trim().toLowerCase()
	const hasDahuaFaceTerminalModel = (item) => DAHUA_FACE_TERMINAL_MODEL_PATTERNS.some(pattern => pattern.test(normalizedDahuaModel(item)))
	const hasDahuaPrefixedFaceTerminalModel = (item) => DAHUA_PREFIXED_FACE_TERMINAL_MODEL_PATTERNS.some(pattern => pattern.test(normalizedDahuaModel(item)))
	const hasDahuaCardControllerModel = (item) => normalizedDahuaModel(item).startsWith('asc')
	const hasHikvisionFaceTerminalModel = (item) => /^ds-k1t/i.test(normalizedDahuaModel(item))
	const hasHikvisionFaceTerminalIdentifier = (identifiers = []) => identifiers.some(value => /^ds-k1t/i.test(value) || value.includes('terminal_hikvision'))

	export const isAccessControlFaceTerminal = (item) => {
		if (!item) return false
		const capabilities = item.capabilities || {}
		if (capabilities.faceDataRecord === false) return false
		if (hasDahuaCardControllerModel(item)) return false
		if (capabilities.faceDataRecord === true) return true

		const fields = normalizedTextFields(item)
		const identifiers = normalizedIdentifierFields(item)
		const isKnownDahuaFaceTerminal = identifiers.some(value => KNOWN_DAHUA_FACE_TERMINAL_IDS.includes(value))
		const isKnownHikvisionFaceTerminal = fields.some(value => value.includes('terminal_hikvision')) || hasHikvisionFaceTerminalModel(item) || hasHikvisionFaceTerminalIdentifier(identifiers)
		const dahuaEvidenceFields = normalizedDahuaEvidenceFields(item)
		const isDahuaTerminal = fields.some(value => value.includes('dahua'))
		const hasExplicitDahuaFaceEvidence = hasKeyword(dahuaEvidenceFields, FACE_TERMINAL_KEYWORDS) && hasKeyword(dahuaEvidenceFields, ACCESS_TERMINAL_KEYWORDS)
		return isKnownDahuaFaceTerminal || isKnownHikvisionFaceTerminal || hasDahuaPrefixedFaceTerminalModel(item) || (isDahuaTerminal && (hasExplicitDahuaFaceEvidence || hasDahuaFaceTerminalModel(item)))
	}

	const deviceId = (device) => device && (device.deviceId || device.device_id || device.serial || device.id)
	const deviceName = (device) => device && (device.name || device.hostname || device.host || deviceId(device))
	const rosterMetadata = (roster) => (roster && (roster.rosterMeta || roster.meta || roster.metadata)) || {}
	const hasBackendOnlyEvidence = (face) => Boolean(face && (
		face.backendOnly === true ||
		face.backend_only === true ||
		face.stale === true ||
		face.isStale === true ||
		face.missingFromTerminal === true ||
		face.inTerminal === false ||
		face.terminal === false ||
		face.inSystem === true && face.inTerminal === false
	))
	const defaultRegistrationStatus = (face, source) => {
		if (face && face.registrationStatus) return face.registrationStatus
		if (source === 'registeredFaces') return 'registered'
		if (source === 'unregisteredFaces') return hasBackendOnlyEvidence(face) ? 'backend_only' : 'terminal_only'
		return 'registered'
	}
	const isTerminalPresenceStatus = (status) => status === 'registered' || status === 'terminal_only'
	const hasIncompleteRosterMetadata = (roster) => {
		const meta = rosterMetadata(roster)
		return Boolean(
			meta.partial === true ||
			meta.truncated === true ||
			roster && (roster.partial === true || roster.truncated === true)
		)
	}
	const rosterConnectionStatus = (roster) => {
		const rawStatus = roster && (roster.status || roster.terminalStatus || roster.connectionStatus || roster.deviceStatus)
		const normalizedStatus = String(rawStatus || '').trim().toLowerCase()
		if (['online', 'connected', 'ok', 'ready'].includes(normalizedStatus)) return 'online'
		if (['offline', 'disconnected', 'unreachable', 'unavailable'].includes(normalizedStatus)) return 'offline'

		const rawReason = roster && (roster.reason || roster.error || roster.message)
		const normalizedReason = String(rawReason || '').toLowerCase()
		if (normalizedReason.includes('terminal query failed') || normalizedReason.includes('offline') || normalizedReason.includes('econnrefused')) return 'offline'

		return ''
	}
	const deviceStateFromRoster = (roster) => {
		const connectionStatus = rosterConnectionStatus(roster)
		if (connectionStatus) return connectionStatus
		if (hasIncompleteRosterMetadata(roster)) return 'partial'
		return 'online'
	}
	const padDatePart = (value) => String(value).padStart(2, '0')
	const dayKey = (date) => `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`
	const dayLabel = (date) => `${padDatePart(date.getMonth() + 1)}/${padDatePart(date.getDate())}`

	export const buildLastDaysAccessEventStats = (events = [], now = new Date(), days = MULTI_FACE_ROSTER_EVENT_DAYS) => {
		const end = new Date(now)
		const start = new Date(end)
		start.setDate(start.getDate() - (days - 1))
		start.setHours(0, 0, 0, 0)

		const buckets = []
		const counts = {}
		for (let index = 0; index < days; index += 1) {
			const date = new Date(start)
			date.setDate(start.getDate() + index)
			const key = dayKey(date)
			counts[key] = 0
			buckets.push({ key, label: dayLabel(date) })
		}

		events.forEach(event => {
			const rawDate = event && (event.occurred_at || event.createdAt)
			const date = rawDate ? new Date(rawDate) : null
			if (!date || Number.isNaN(date.getTime())) return
			const key = dayKey(date)
			if (Object.prototype.hasOwnProperty.call(counts, key)) counts[key] += 1
		})

		return {
			labels: buckets.map(bucket => bucket.label),
			counts: buckets.map(bucket => counts[bucket.key]),
		}
	}

	export const aggregateFaceRosters = (rosters = []) => {
		const people = {}

		rosters.forEach(({ device, roster }) => {
			const terminalId = deviceId(device)
			const terminalName = (roster && roster.name) || deviceName(device) || 'Terminal'
			const faces = []
			if (roster && Array.isArray(roster.registeredFaces)) faces.push(...roster.registeredFaces.map(face => ({ face, source: 'registeredFaces' })))
			if (roster && Array.isArray(roster.unregisteredFaces)) faces.push(...roster.unregisteredFaces.map(face => ({ face, source: 'unregisteredFaces' })))
			if (!faces.length && roster && Array.isArray(roster.faces)) faces.push(...roster.faces.map(face => ({ face, source: 'faces' })))

			faces.forEach(({ face, source }, index) => {
				const fpid = face && face.FPID ? String(face.FPID) : ''
				const key = fpid || `${terminalId || 'terminal'}:${face && face.name ? face.name : 'person'}:${index}`
				if (!people[key]) {
					people[key] = {
						key,
						FPID: fpid,
						name: (face && face.name) || '',
						imageBase64: (face && face.imageBase64) || '',
						terminals: [],
						statuses: [],
						faces: [],
						inSystem: false,
						inTerminal: false,
						userId: '',
						backendUserId: '',
					}
				}

				const person = people[key]
				if (!person.name && face && face.name) person.name = face.name
				if (!person.imageBase64 && face && face.imageBase64) person.imageBase64 = face.imageBase64
				if (!person.userId && face && face.userId) person.userId = face.userId
				if (!person.backendUserId && face && face.backendUserId) person.backendUserId = face.backendUserId
				const status = defaultRegistrationStatus(face, source)
				if (isTerminalPresenceStatus(status) && !person.terminals.some(terminal => terminal.id === terminalId)) {
					person.terminals.push({ id: terminalId || terminalName, name: terminalName })
				}
				person.statuses.push(status)
				person.faces.push({ ...face, registrationStatus: status, terminalId, terminalName })
				person.inTerminal = person.inTerminal || isTerminalPresenceStatus(status)
				person.inSystem = person.inSystem || status === 'registered' || status === 'backend_only'
			})
		})

		return Object.values(people).sort((a, b) => String(a.name || a.FPID).localeCompare(String(b.name || b.FPID)))
	}

	export default {
		name: 'access_control_multi_face_roster',
		components: { TrainedFaceEnrollmentModal },
		props: {
			info_to_component: { type: Object, default: () => ({}) },
			hosts: { type: Object, default: () => ({}) },
			info_device: { type: Object, default: () => ({}) },
		},
		data: () => ({
			loading: false,
			loadError: '',
			filterQuery: '',
			groupMode: 'person',
			devices: [],
			deviceStates: [],
			people: [],
			unavailableImages: {},
			selectedPeopleKeys: {},
			importDialog: false,
			faceLists: [],
			selectedListId: '',
			listsLoading: false,
			importLoading: false,
			importError: '',
			importSuccess: '',
			sourceSelections: {},
			personDetailDialog: false,
			selectedPerson: null,
			personEventsLoading: false,
			personEventsError: '',
			personEvents: [],
			personEventsChart: null,
			actionLoading: false,
			actionError: '',
			actionSuccess: '',
			addPersonDialog: false,
			addPersonListIds: [],
			moveTargetTerminalIds: [],
			confirmDialog: false,
			confirmTitle: '',
			confirmMessage: '',
			confirmLabel: '',
			confirmHandler: null,
		}),
		computed: {
			groupOptions(){
				return [
					{ text: this.mlText('multi_face_roster_group_person'), value: 'person' },
					{ text: this.mlText('multi_face_roster_group_terminal'), value: 'terminal' },
					{ text: this.mlText('multi_face_roster_registered'), value: 'registered' },
					{ text: this.mlText('multi_face_roster_missing_import'), value: 'terminal_only' },
				]
			},
			config(){
				return this.info_to_component && Object.keys(this.info_to_component).length ? this.info_to_component : (this.hosts || this.info_device || {})
			},
			terminalCountLabel(){
				const count = this.devices.length
				return this.formatText('multi_face_roster_terminal_count', [count])
			},
			summaryCountLabel(){
				return this.formatText('multi_face_roster_summary_counts', [this.registeredCount, this.terminalOnlyCount])
			},
			filterNeedle(){
				return String(this.filterQuery || '').trim().toLowerCase()
			},
			filteredPeople(){
				if (!this.filterNeedle) return this.people
				return this.people.filter(person => {
					const name = String(person.name || '').toLowerCase()
					const fpid = String(person.FPID || '').toLowerCase()
					return name.indexOf(this.filterNeedle) !== -1 || fpid.indexOf(this.filterNeedle) !== -1
				})
			},
			registeredCount(){
				return this.people.filter(person => this.isRegistered(person)).length
			},
			terminalOnlyCount(){
				return this.people.filter(person => this.isTerminalOnly(person)).length
			},
			partialErrorText(){
				const failed = this.deviceStates.filter(state => state.status === 'error' || state.status === 'offline')
				const incomplete = this.deviceStates.filter(state => state.status === 'partial')
				const messages = []
				if (failed.length) messages.push(this.formatText('multi_face_roster_failed_terminals', [failed.length]))
				if (incomplete.length) {
					const terminalNames = incomplete.map(state => state.name).join(', ')
					messages.push(this.formatText('multi_face_roster_partial_terminals', [terminalNames]))
				}
				return messages.join(' ')
			},
			selectablePeople(){
				return this.people.filter(person => this.isSelectablePerson(person))
			},
			hasSelectablePeople(){
				return this.selectablePeople.length > 0
			},
			selectedPeople(){
				return this.selectablePeople.filter(person => this.isSelected(person))
			},
			selectedCount(){
				return this.selectedPeople.length
			},
			selectedCountLabel(){
				return this.formatText('multi_face_roster_selected_count', [this.selectedCount])
			},
			selectedReadyCountLabel(){
				return this.formatText('multi_face_roster_ready_count', [this.selectedCount])
			},
			ambiguousSelectedPeople(){
				return this.selectedPeople.filter(person => this.terminalOnlyFaces(person).length > 1)
			},
			faceListOptions(){
				return this.faceLists.map(list => ({ text: list.name || this.formatText('multi_face_roster_list_fallback', [list.id]), value: list.id }))
			},
			terminalMirrorLists(){
				return this.faceLists.filter(list => this.isTerminalMirrorList(list))
			},
			canConfirmImport(){
				return Boolean(this.selectedCount && this.selectedListId && !this.importLoading && this.selectedPeople.every(person => this.resolveSourceFace(person)))
			},
			groupedSections(){
				if (this.groupMode === 'terminal') return this.groupByTerminal()
				if (this.groupMode === 'registered') {
					return [this.buildSection('registered', this.mlText('multi_face_roster_registered'), this.mlText('multi_face_roster_registered_subtitle'), this.filteredPeople.filter(person => this.isRegistered(person)))]
						.filter(section => section.people.length)
				}
				if (this.groupMode === 'terminal_only') {
					return [this.buildSection('terminal-only', this.mlText('multi_face_roster_missing_import'), this.mlText('multi_face_roster_missing_import_subtitle'), this.filteredPeople.filter(person => this.isTerminalOnly(person)))]
						.filter(section => section.people.length)
				}
				return [this.buildSection('person', this.mlText('multi_face_roster_group_person'), this.mlText('multi_face_roster_person_view_subtitle'), this.filteredPeople)]
					.filter(section => section.people.length)
			},
			selectedPersonImageSource(){
				return this.personImageSource(this.selectedPerson)
			},
			personEventStats(){
				return buildLastDaysAccessEventStats(this.personEvents)
			},
			personEventTotal(){
				return this.personEventStats.counts.reduce((total, count) => total + count, 0)
			},
			hasPersonEventCounts(){
				return this.personEventTotal > 0
			},
			selectedPersonMoveTargetOptions(){
				return this.moveTargetOptions(this.selectedPerson)
			},
			selectedPersonMoveTargetLabel(){
				return this.isBackendOnlyPerson(this.selectedPerson) ? this.mlText('multi_face_roster_add_to_terminal_target_label') : this.mlText('multi_face_roster_copy_or_move_target_label')
			},
			selectedPersonMoveNoDestinationsText(){
				return this.isBackendOnlyPerson(this.selectedPerson) ? this.mlText('multi_face_roster_add_to_terminal_no_destinations') : this.mlText('multi_face_roster_move_no_destinations')
			},
			selectedPersonTerminalActionLabel(){
				return this.isBackendOnlyPerson(this.selectedPerson) ? this.mlText('multi_face_roster_add_to_terminal') : this.mlText('multi_face_roster_move_person')
			},
			canConfirmMovePerson(){
				return Boolean(this.moveTargetTerminalIds.length && this.selectedMoveTargetDevices().length === this.moveTargetTerminalIds.length && !this.actionLoading)
			},
		},
		watch: {
			selectedPerson(newPerson, oldPerson){
				if (!newPerson || !oldPerson || newPerson.key !== oldPerson.key) {
					this.moveTargetTerminalIds = []
					return
				}
				this.sanitizeMoveTargetSelection(newPerson)
			},
			moveTargetTerminalIds(){
				this.sanitizeMoveTargetSelection(this.selectedPerson)
			},
			devices: {
				deep: true,
				handler(){
					this.sanitizeMoveTargetSelection(this.selectedPerson)
				},
			},
			faceLists: {
				deep: true,
				handler(){
					this.sanitizeMoveTargetSelection(this.selectedPerson)
				},
			},
		},
		mounted(){
			this.loadRosters()
			this.loadFaceLists()
		},
		beforeDestroy(){
			this.destroyPersonEventsChart()
		},
		methods: {
			mlText(key){
				if (this.$ml && typeof this.$ml.get === 'function') return this.$ml.get(key)
				return key
			},
			formatText(key, values = []){
				return values.reduce((text, value, index) => String(text).replace(new RegExp(`\\{${index}\\}`, 'g'), value), this.mlText(key))
			},
			peopleCountLabel(count){
				return this.formatText('multi_face_roster_people_count', [count])
			},
			yesNoText(value){
				return value ? this.mlText('multi_face_roster_yes') : this.mlText('multi_face_roster_no')
			},
			async loadRosters(){
				this.loading = true
				this.loadError = ''
				this.deviceStates = []
				try {
					const devices = await this.resolveDevices()
					this.devices = devices
					if (!devices.length) {
						this.people = []
						this.loadError = this.mlText('multi_face_roster_no_terminals')
						return
					}

					const results = await Promise.all(devices.map(device => this.loadDeviceRoster(device)))
					this.people = aggregateFaceRosters(results.filter(result => result.roster))
					this.pruneSelection()
				} catch (error) {
					this.people = []
					this.loadError = this.mlText('multi_face_roster_load_error')
				} finally {
					this.refreshSelectedPersonFromRoster()
					this.loading = false
				}
			},
			async resolveDevices(){
				const selected = this.selectedDeviceConfig()
				if (selected.length) return selected

				const list = []
				let page = 1
				let hasMore = true
				while (hasMore) {
					const res = await AccessControlService.listDevices({ page, limit: MULTI_FACE_ROSTER_DEVICE_LIST_LIMIT })
					const payload = res && res.data ? res.data : res
					if (payload && Array.isArray(payload.results)) {
						list.push(...payload.results)
						hasMore = payload.total > list.length
					} else if (payload && Array.isArray(payload.rows)) {
						list.push(...payload.rows)
						hasMore = Boolean(payload.pages) && payload.page < payload.pages
						if (payload.page !== undefined) page = payload.page
					} else {
						list.push(...(Array.isArray(payload) ? payload : []))
						hasMore = false
					}
					if (hasMore) page += 1
				}
				return list.filter(isAccessControlFaceTerminal)
			},
			selectedDeviceConfig(){
				const devices = this.config.devices || this.config.selectedDevices || this.config.terminals || []
				if (!Array.isArray(devices)) return []
				return devices.map(item => typeof item === 'string' ? { deviceId: item, name: item } : item).filter(Boolean)
			},
			async loadDeviceRoster(device){
				const id = deviceId(device)
				const name = deviceName(device) || id || this.mlText('multi_face_roster_terminal')
				this.deviceStates.push({ id: id || name, name, status: 'loading' })
				try {
					const res = await AccessControlService.getFaceRoster(id, MULTI_FACE_ROSTER_LIMIT)
					const roster = res && res.data ? res.data : null
					this.setDeviceState(id || name, { status: deviceStateFromRoster(roster), reason: roster && roster.reason ? String(roster.reason) : '' })
					return { device, roster }
				} catch (error) {
					this.setDeviceState(id || name, { status: 'error' })
					return { device, roster: null, error }
				}
			},
			setDeviceState(id, patch){
				const index = this.deviceStates.findIndex(state => state.id === id)
				if (index >= 0) this.$set(this.deviceStates, index, { ...this.deviceStates[index], ...patch })
			},
			deviceStateText(state){
				if (state.status === 'online' || state.status === 'loaded') return this.mlText('multi_face_roster_state_connected')
				if (state.status === 'offline') return this.mlText('multi_face_roster_state_disconnected')
				if (state.status === 'partial') return this.mlText('multi_face_roster_state_partial')
				if (state.status === 'error') return this.mlText('multi_face_roster_state_error')
				return this.mlText('multi_face_roster_state_loading')
			},
			deviceStateTitle(state){
				if (!state || !state.reason) return this.deviceStateText(state)
				return `${this.deviceStateText(state)} — ${this.mlText('multi_face_roster_state_diagnostic')}: ${state.reason}`
			},
			deviceStateColor(state){
				if (state.status === 'online' || state.status === 'loaded') return 'success'
				if (state.status === 'offline') return 'error'
				if (state.status === 'partial') return 'warning'
				if (state.status === 'error') return 'warning'
				return 'primary'
			},
			buildSection(key, title, subtitle, people){
				return { key, title, subtitle, people }
			},
			groupByTerminal(){
				return this.devices.map(device => {
					const id = deviceId(device)
					const name = deviceName(device) || id || this.mlText('multi_face_roster_terminal')
					return this.buildSection(
						`terminal-${id || name}`,
						name,
						this.mlText('multi_face_roster_terminal'),
						this.filteredPeople.filter(person => person.terminals.some(terminal => terminal.id === id || terminal.name === name))
					)
				}).filter(section => section.people.length)
			},
			isRegistered(person){
				return person && person.statuses.includes('registered')
			},
			isBackendOnlyPerson(person){
				return person && person.statuses.includes('backend_only') && !this.isRegistered(person)
			},
			canOpenPersonDetail(person){
				return this.isRegistered(person) || this.isBackendOnlyPerson(person)
			},
			isTerminalOnly(person){
				return person && person.statuses.includes('terminal_only') && !person.statuses.includes('registered')
			},
			isSelectablePerson(person){
				return this.isTerminalOnly(person)
			},
			statusText(person){
				if (this.isRegistered(person)) return this.mlText('multi_face_roster_registered_single')
				if (person && person.statuses.includes('terminal_only')) return this.mlText('multi_face_roster_missing_import')
				if (person && person.statuses.includes('backend_only')) return this.mlText('multi_face_roster_backend_only')
				return this.mlText('multi_face_roster_registered_single')
			},
			statusColor(person){
				if (this.isRegistered(person)) return 'success'
				if (person && person.statuses.includes('terminal_only')) return 'error'
				return 'warning'
			},
			statusSummaryText(person){
				return `${this.statusText(person)} · ${this.mlText('multi_face_roster_system')}: ${this.yesNoText(person && person.inSystem)} · ${this.mlText('multi_face_roster_terminal')}: ${this.yesNoText(person && person.inTerminal)}`
			},
			personImageSource(person){
				if (!person || !person.imageBase64 || this.unavailableImages[person.key]) return ''
				const image = String(person.imageBase64)
				return image.indexOf('data:') === 0 ? image : `data:image/jpeg;base64,${image}`
			},
			markImageUnavailable(person){
				if (person && person.key) this.$set(this.unavailableImages, person.key, true)
			},
			personPhotoAlt(person){
				return this.formatText('multi_face_roster_photo_alt', [person && person.name ? person.name : this.mlText('multi_face_roster_unnamed_person')])
			},
			selectionKey(person){
				return person && person.key ? String(person.key) : ''
			},
			isSelected(person){
				const key = this.selectionKey(person)
				return Boolean(key && this.selectedPeopleKeys[key])
			},
			selectionLabel(person){
				return this.formatText(this.isSelected(person) ? 'multi_face_roster_unselect_person' : 'multi_face_roster_select_person', [person && person.name ? person.name : this.mlText('multi_face_roster_unnamed_person')])
			},
			toggleSelection(person){
				if (!this.isSelectablePerson(person)) return
				const key = this.selectionKey(person)
				if (!key) return
				if (this.selectedPeopleKeys[key]) {
					this.$delete(this.selectedPeopleKeys, key)
					this.$delete(this.sourceSelections, key)
				} else {
					this.$set(this.selectedPeopleKeys, key, true)
					const faces = this.terminalOnlyFaces(person)
					if (faces.length === 1) this.$set(this.sourceSelections, key, faces[0].terminalId)
				}
				this.importSuccess = ''
			},
			clearSelection(){
				this.selectedPeopleKeys = {}
				this.sourceSelections = {}
				this.importSuccess = ''
			},
			pruneSelection(){
				const available = new Set(this.selectablePeople.map(person => this.selectionKey(person)).filter(Boolean))
				Object.keys(this.selectedPeopleKeys).forEach(key => {
					if (!available.has(key)) {
						this.$delete(this.selectedPeopleKeys, key)
						this.$delete(this.sourceSelections, key)
					}
				})
			},
			terminalOnlyFaces(person){
				return person && Array.isArray(person.faces) ? person.faces.filter(face => face.registrationStatus === 'terminal_only') : []
			},
			sourceOptions(person){
				return this.terminalOnlyFaces(person).map(face => ({ text: face.terminalName || face.terminalId || this.mlText('multi_face_roster_terminal'), value: face.terminalId }))
			},
			setSourceSelection(person, terminalId){
				if (!person || !person.key) return
				this.$set(this.sourceSelections, person.key, terminalId)
			},
			resolveSourceFace(person){
				const faces = this.terminalOnlyFaces(person)
				if (faces.length === 1) return faces[0]
				const selectedTerminalId = this.sourceSelections[person.key]
				return faces.find(face => face.terminalId === selectedTerminalId) || null
			},
			async openImportDialog(){
				if (!this.selectedCount) return
				this.importDialog = true
				this.importError = ''
				await this.loadFaceLists()
			},
			openPersonDetail(person){
				if (!this.canOpenPersonDetail(person)) return
				this.selectedPerson = person
				this.personDetailDialog = true
				this.moveTargetTerminalIds = []
				this.loadPersonEvents(person)
			},
			closePersonDetail(){
				this.personDetailDialog = false
				this.selectedPerson = null
				this.personEvents = []
				this.personEventsError = ''
				this.destroyPersonEventsChart()
			},
			onPersonDetailDialogInput(value){
				if (!value) this.closePersonDetail()
			},
			personEventsRange(){
				const end = new Date()
				end.setHours(23, 59, 59, 999)
				const start = new Date(end)
				start.setDate(start.getDate() - (MULTI_FACE_ROSTER_EVENT_DAYS - 1))
				start.setHours(0, 0, 0, 0)
				return { start: start.toISOString(), end: end.toISOString() }
			},
			async loadPersonEvents(person){
				if (!person || !person.FPID) return
				this.personEventsLoading = true
				this.personEventsError = ''
				this.personEvents = []
				this.destroyPersonEventsChart()
				try {
					const range = this.personEventsRange()
					const response = await AccessControlService.findEvents({
						FPID: person.FPID,
						start: range.start,
						end: range.end,
						timeZone: this.$timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
					}, { page: 1, limit: MULTI_FACE_ROSTER_EVENT_LIMIT })
					const payload = response && response.data ? response.data : response
					this.personEvents = payload && Array.isArray(payload.rows) ? payload.rows : []
				} catch (error) {
					this.personEventsError = this.mlText('multi_face_roster_events_error')
				} finally {
					this.personEventsLoading = false
					if (!this.personEventsError) this.$nextTick(this.renderPersonEventsChart)
				}
			},
			renderPersonEventsChart(){
				if (!this.hasPersonEventCounts || !this.$refs.personEventsChart) return
				this.destroyPersonEventsChart()
				const context = this.$refs.personEventsChart.getContext('2d')
				this.personEventsChart = new Chart(context, {
					type: 'bar',
					data: {
						labels: this.personEventStats.labels,
						datasets: [{
							label: this.mlText('multi_face_roster_events_chart_label'),
							data: this.personEventStats.counts,
							backgroundColor: 'rgba(66, 165, 245, 0.45)',
							borderColor: '#42A5F5',
							borderWidth: 1,
						}],
					},
					options: {
						responsive: true,
						maintainAspectRatio: false,
						legend: { labels: { fontColor: '#FFFFFF' } },
						scales: {
							xAxes: [{ ticks: { fontColor: '#FFFFFF', maxRotation: 0 }, gridLines: { color: 'rgba(255,255,255,0.10)' } }],
							yAxes: [{ ticks: { beginAtZero: true, precision: 0, fontColor: '#FFFFFF' }, gridLines: { color: 'rgba(255,255,255,0.10)' } }],
						},
					},
				})
			},
			destroyPersonEventsChart(){
				if (this.personEventsChart && typeof this.personEventsChart.destroy === 'function') this.personEventsChart.destroy()
				this.personEventsChart = null
			},
			closeImportDialog(){
				if (this.importLoading) return
				this.importDialog = false
			},
			async loadFaceLists(){
				this.listsLoading = true
				this.importError = ''
				try {
					let rows = []
					if (typeof ListService.getListFor === 'function') {
						rows = await ListService.getListFor(1)
					} else {
						const res = await ListService.getList(1)
						rows = res && res.data && Array.isArray(res.data.rows) ? res.data.rows : []
					}
					this.faceLists = rows.filter(list => !list.type || String(list.type) === 'face')
					if (!this.selectedListId && this.faceLists.length) this.selectedListId = this.faceLists[0].id
				} catch (error) {
					this.faceLists = []
					this.importError = this.mlText('multi_face_roster_lists_error')
				} finally {
					this.listsLoading = false
				}
			},
			isTerminalMirrorList(list = {}){
				return String(list.managedBy || list.managed_by || '').toLowerCase() === 'access_terminal' && Boolean(list.ownerDeviceId || list.owner_device_id)
			},
			terminalMirrorListForDevice(deviceId){
				const normalizedDeviceId = deviceId === undefined || deviceId === null ? '' : String(deviceId)
				return this.terminalMirrorLists.find(list => String(list.ownerDeviceId || list.owner_device_id) === normalizedDeviceId) || null
			},
			personEntityId(person = {}){
				if (person.backendUserId) return person.backendUserId
				if (person.userId) return person.userId
				const face = Array.isArray(person.faces) ? person.faces.find(item => item.backendUserId || item.userId) : null
				return face ? (face.backendUserId || face.userId) : (person.FPID || '')
			},
			findRosterPersonByIdentity(person){
				if (!person) return null
				const key = person.key ? String(person.key) : ''
				const fpid = person.FPID ? String(person.FPID) : ''
				const entity = this.personEntityId(person)

				return this.people.find(candidate => key && String(candidate.key) === key) ||
					this.people.find(candidate => fpid && String(candidate.FPID) === fpid) ||
					this.people.find(candidate => entity && String(this.personEntityId(candidate)) === String(entity)) ||
					null
			},
			refreshSelectedPersonFromRoster(){
				if (!this.personDetailDialog || !this.selectedPerson) return
				const refreshedPerson = this.findRosterPersonByIdentity(this.selectedPerson)
				if (!refreshedPerson) return
				this.selectedPerson = refreshedPerson
				this.sanitizeMoveTargetSelection(refreshedPerson)
			},
			openAddPersonDialog(){
				this.actionError = ''
				this.actionSuccess = ''
				if (!this.faceLists.length) this.loadFaceLists()
				const firstTerminalList = this.terminalMirrorLists[0]
				this.addPersonListIds = firstTerminalList && firstTerminalList.id ? [String(firstTerminalList.id)] : []
				this.addPersonDialog = true
			},
			async handlePersonAdded(){
				this.addPersonDialog = false
				this.actionSuccess = this.mlText('multi_face_roster_add_success')
				await this.loadRosters()
			},
			moveTargetOptions(person){
				const currentTerminalIds = new Set(((person && person.terminals) || []).map(terminal => String(terminal.id)))
				return this.devices
					.filter(device => {
						const id = deviceId(device)
						return this.terminalMirrorListForDevice(id) && !currentTerminalIds.has(String(id))
					})
					.map(device => ({ text: deviceName(device) || deviceId(device), value: deviceId(device) }))
			},
			allowedMoveTargetIds(person){
				return new Set(this.moveTargetOptions(person).map(option => String(option.value)))
			},
			sanitizeMoveTargetSelection(person){
				if (!person || !Array.isArray(this.moveTargetTerminalIds)) return
				const allowed = this.allowedMoveTargetIds(person)
				const selected = this.moveTargetTerminalIds.filter(id => allowed.has(String(id)))
				const changed = selected.length !== this.moveTargetTerminalIds.length || selected.some((id, index) => id !== this.moveTargetTerminalIds[index])
				if (changed) this.moveTargetTerminalIds = selected
			},
			selectedMoveTargetDevices(){
				const allowed = this.allowedMoveTargetIds(this.selectedPerson)
				const selected = new Set((this.moveTargetTerminalIds || []).map(id => String(id)))
				return this.devices.filter(device => selected.has(String(deviceId(device))) && allowed.has(String(deviceId(device))))
			},
			selectedMoveTargetLists(){
				return this.uniqueById(this.selectedMoveTargetDevices().map(device => this.terminalMirrorListForDevice(deviceId(device))).filter(Boolean))
			},
			uniqueById(items){
				const seen = new Set()
				return items.filter(item => {
					const id = item && item.id !== undefined && item.id !== null ? String(item.id) : ''
					if (!id || seen.has(id)) return false
					seen.add(id)
					return true
				})
			},
			showConfirm({ title, message, confirmLabel, handler }){
				this.confirmTitle = title
				this.confirmMessage = message
				this.confirmLabel = confirmLabel || this.mlText('multi_face_roster_confirm')
				this.confirmHandler = handler
				this.confirmDialog = true
			},
			closeConfirmDialog(){
				if (this.actionLoading) return
				this.confirmDialog = false
				this.confirmLabel = ''
				this.confirmHandler = null
			},
			confirmRemoveFromTerminal(person, terminal){
				this.showConfirm({
					title: this.mlText('multi_face_roster_remove_confirm_title'),
					message: this.formatText('multi_face_roster_remove_confirm_message', [person.name || person.FPID || this.mlText('multi_face_roster_unnamed_person'), terminal.name || terminal.id]),
					handler: () => this.removePersonFromTerminal(person, terminal),
				})
			},
			confirmMovePerson(person){
				this.sanitizeMoveTargetSelection(person)
				const targets = this.selectedMoveTargetDevices()
				if (!targets.length || targets.length !== this.moveTargetTerminalIds.length) return
				const targetNames = targets.map(device => deviceName(device) || deviceId(device)).join(', ')
				this.showConfirm({
					title: this.mlText('multi_face_roster_move_confirm_title'),
					message: this.formatText('multi_face_roster_move_confirm_message', [person.name || person.FPID || this.mlText('multi_face_roster_unnamed_person'), targetNames]),
					confirmLabel: this.mlText('multi_face_roster_move_confirm'),
					handler: () => this.movePersonToTerminals(person, this.moveTargetTerminalIds),
				})
			},
			confirmAddPersonToTerminals(person){
				this.sanitizeMoveTargetSelection(person)
				const targets = this.selectedMoveTargetDevices()
				if (!targets.length || targets.length !== this.moveTargetTerminalIds.length) return
				const targetNames = targets.map(device => deviceName(device) || deviceId(device)).join(', ')
				this.showConfirm({
					title: this.mlText('multi_face_roster_add_to_terminal_confirm_title'),
					message: this.formatText('multi_face_roster_add_to_terminal_confirm_message', [person.name || person.FPID || this.mlText('multi_face_roster_unnamed_person'), targetNames]),
					confirmLabel: this.mlText('multi_face_roster_add_to_terminal_confirm'),
					handler: () => this.addPersonToTerminals(person, this.moveTargetTerminalIds),
				})
			},
			confirmCopyPersonToTerminals(person){
				this.sanitizeMoveTargetSelection(person)
				const targets = this.selectedMoveTargetDevices()
				if (!targets.length || targets.length !== this.moveTargetTerminalIds.length) return
				const targetNames = targets.map(device => deviceName(device) || deviceId(device)).join(', ')
				this.showConfirm({
					title: this.mlText('multi_face_roster_copy_confirm_title'),
					message: this.formatText('multi_face_roster_copy_confirm_message', [person.name || person.FPID || this.mlText('multi_face_roster_unnamed_person'), targetNames]),
					confirmLabel: this.mlText('multi_face_roster_copy_confirm'),
					handler: () => this.copyPersonToTerminals(person, this.moveTargetTerminalIds),
				})
			},
			confirmSelectedPersonTerminalAction(person){
				if (this.isBackendOnlyPerson(person)) {
					this.confirmAddPersonToTerminals(person)
					return
				}
				this.confirmMovePerson(person)
			},
			confirmDeleteCompletely(person){
				this.showConfirm({
					title: this.mlText('multi_face_roster_delete_confirm_title'),
					message: this.formatText('multi_face_roster_delete_confirm_message', [person.name || person.FPID || this.mlText('multi_face_roster_unnamed_person')]),
					handler: () => this.deletePersonCompletely(person),
				})
			},
			async runConfirmedAction(){
				if (!this.confirmHandler) return
				this.actionLoading = true
				this.actionError = ''
				this.actionSuccess = ''
				try {
					await this.confirmHandler()
					this.confirmDialog = false
					this.confirmLabel = ''
					this.confirmHandler = null
					await this.loadRosters()
				} catch (error) {
					console.warn('AccessControlMultiFaceRoster action failed', error)
					this.actionError = this.mlText('multi_face_roster_action_error')
				} finally {
					this.actionLoading = false
				}
			},
			async findTerminalMembership(person, terminal){
				const terminalList = this.terminalMirrorListForDevice(terminal && terminal.id)
				if (!terminalList) throw new Error(this.mlText('multi_face_roster_terminal_list_missing'))
				const entity = this.personEntityId(person)
				if (!entity) throw new Error(this.mlText('multi_face_roster_person_id_missing'))
				const response = await ListService.findListMembers({ type: 'face', where: { entity } })
				const rows = response && response.data && Array.isArray(response.data.rows) ? response.data.rows : []
				return rows.find(row => String(row.listId) === String(terminalList.id) && String(row.entity) === String(entity)) || null
			},
			async findTerminalMemberships(person, terminals){
				const entity = this.personEntityId(person)
				if (!entity) throw new Error(this.mlText('multi_face_roster_person_id_missing'))
				const terminalLists = this.uniqueById((terminals || []).map(terminal => this.terminalMirrorListForDevice(terminal && terminal.id)).filter(Boolean))
				if (!terminalLists.length) throw new Error(this.mlText('multi_face_roster_terminal_list_missing'))
				const listIds = new Set(terminalLists.map(list => String(list.id)))
				const response = await ListService.findListMembers({ type: 'face', where: { entity } })
				const rows = response && response.data && Array.isArray(response.data.rows) ? response.data.rows : []
				return rows.filter(row => listIds.has(String(row.listId)) && String(row.entity) === String(entity))
			},
			async removePersonFromTerminal(person, terminal){
				const member = await this.findTerminalMembership(person, terminal)
				if (!member || !member.id) throw new Error(this.mlText('multi_face_roster_membership_missing'))
				await ListService.removeListMember(member.id)
				this.actionSuccess = this.formatText('multi_face_roster_remove_success', [terminal.name || terminal.id])
			},
			async movePersonToTerminals(person, targetTerminalIds){
				const origins = person.terminals || []
				const targetLists = this.uniqueById((targetTerminalIds || []).map(terminalId => this.terminalMirrorListForDevice(terminalId)).filter(Boolean))
				const originLists = this.uniqueById(origins.map(terminal => this.terminalMirrorListForDevice(terminal && terminal.id)).filter(Boolean))
				const entity = this.personEntityId(person)
				if (!origins.length || !originLists.length || !targetLists.length || !entity) throw new Error(this.mlText('multi_face_roster_action_error'))
				const originMemberships = await this.findTerminalMemberships(person, origins)
				if (originMemberships.length !== originLists.length) throw new Error(this.mlText('multi_face_roster_membership_missing'))
				await Promise.all(originMemberships.map(member => ListService.removeListMember(member.id)))
				await Promise.all(targetLists.map(list => ListService.addListMembers({ listId: list.id, entity: [entity], motive: 'Moved between terminal mirror lists', type: 'face' })))
				this.actionSuccess = this.mlText('multi_face_roster_move_success')
			},
			async addPersonToTerminals(person, targetTerminalIds){
				const targetLists = this.uniqueById((targetTerminalIds || []).map(terminalId => this.terminalMirrorListForDevice(terminalId)).filter(Boolean))
				const entity = this.personEntityId(person)
				if (!targetLists.length || !entity) throw new Error(this.mlText('multi_face_roster_action_error'))
				await Promise.all(targetLists.map(list => ListService.addListMembers({ listId: list.id, entity: [entity], motive: 'Added backend-only person to terminal mirror lists', type: 'face' })))
				this.actionSuccess = this.mlText('multi_face_roster_add_to_terminal_success')
			},
			async copyPersonToTerminals(person, targetTerminalIds){
				const targetLists = this.uniqueById((targetTerminalIds || []).map(terminalId => this.terminalMirrorListForDevice(terminalId)).filter(Boolean))
				const entity = this.personEntityId(person)
				if (!targetLists.length || !entity) throw new Error(this.mlText('multi_face_roster_action_error'))
				await Promise.all(targetLists.map(list => ListService.addListMembers({ listId: list.id, entity: [entity], motive: 'Copied to terminal mirror lists', type: 'face' })))
				this.actionSuccess = this.mlText('multi_face_roster_copy_success')
			},
			async deletePersonCompletely(person){
				const removals = (person.terminals || []).map(terminal => this.removePersonFromTerminal(person, terminal))
				await Promise.all(removals)
				const entity = this.personEntityId(person)
				if (entity && (person.inSystem || person.backendUserId || person.userId)) await FaceService.deleteTrainedUser(entity)
				this.closePersonDetail()
				this.actionSuccess = this.mlText('multi_face_roster_delete_success')
			},
			groupImportPayloads(){
				return this.selectedPeople.reduce((groups, person) => {
					const sourceFace = this.resolveSourceFace(person)
					if (!sourceFace || !sourceFace.terminalId) return groups
					if (!groups[sourceFace.terminalId]) groups[sourceFace.terminalId] = []
					groups[sourceFace.terminalId].push({
						FPID: sourceFace.FPID || person.FPID,
						name: sourceFace.name || person.name,
						imageBase64: sourceFace.imageBase64 || person.imageBase64 || '',
					})
					return groups
				}, {})
			},
			async confirmImport(){
				if (!this.canConfirmImport) return
				this.importLoading = true
				this.importError = ''
				this.importSuccess = ''
				try {
					const grouped = this.groupImportPayloads()
					const entries = Object.entries(grouped)
					const settled = await Promise.allSettled(entries.map(([sourceDeviceId, faces]) => AccessControlService.importFaceRoster(sourceDeviceId, { listId: this.selectedListId, faces })))
					const summary = settled.reduce((acc, result, index) => {
						const faces = entries[index][1]
						if (result.status === 'rejected') {
							acc.failed += faces.length
							return acc
						}
						const payload = result.value && result.value.data ? result.value.data : result.value
						const importResults = payload && Array.isArray(payload.results) ? payload.results : []
						if (!importResults.length) {
							acc.imported += faces.length
							return acc
						}
						importResults.forEach(item => {
							if (item && item.status === 'imported') acc.imported += 1
							else acc.failed += 1
						})
						return acc
					}, { imported: 0, failed: 0 })

					if (summary.imported) {
						this.importDialog = false
						this.clearSelection()
						this.importSuccess = summary.failed
							? this.formatText('multi_face_roster_import_partial_success', [summary.imported, summary.failed])
							: this.formatText('multi_face_roster_import_success', [summary.imported])
						await this.loadRosters()
						return
					}

					this.importError = summary.failed
						? this.formatText('multi_face_roster_import_failed_count', [summary.failed])
						: this.mlText('multi_face_roster_import_error')
				} catch (error) {
					this.importError = this.mlText('multi_face_roster_import_error')
				} finally {
					this.importLoading = false
				}
			},
		},
	}
</script>

<style scoped>
	.access-control-multi-face-roster,
	.access-control-multi-face-card{
		height: 100%;
		min-height: 0;
		overflow: hidden;
	}

	.access-control-multi-face-card{
		border-radius: 10px;
		display: flex;
		flex-direction: column;
	}

	.access-control-multi-face-header{
		align-items: center;
		border-bottom: thin solid currentColor;
		gap: 8px;
		min-height: 38px;
	}

	.access-control-multi-face-header-icon{
		align-items: center;
		border: thin solid currentColor;
		border-radius: 8px;
		display: flex;
		height: 24px;
		justify-content: center;
		width: 24px;
	}

	.access-control-multi-face-header-copy,
	.access-control-multi-face-person-info{
		min-width: 0;
	}

	.access-control-multi-face-title{
		font-size: 0.78rem;
		font-weight: 700;
		line-height: 1.1;
	}

	.access-control-multi-face-subtitle{
		font-size: 0.64rem;
		opacity: 0.72;
	}

	.access-control-multi-face-refresh{
		color: inherit !important;
	}

	.access-control-multi-face-body{
		display: flex;
		flex: 1 1 auto;
		flex-direction: column;
		min-height: 0;
		overflow: hidden;
	}

	.access-control-multi-face-toolbar{
		align-items: center;
		border: thin solid currentColor;
		border-radius: 10px;
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
		margin-bottom: 6px;
		padding: 6px 8px;
	}

	.access-control-multi-face-summary{
		flex: 1 1 170px;
		min-width: 150px;
	}

	.access-control-multi-face-selection-actions{
		align-items: center;
		display: flex;
		flex: 1 1 260px;
		flex-wrap: wrap;
		gap: 6px;
		justify-content: flex-end;
	}

	.access-control-multi-face-count{
		font-size: 0.82rem;
		font-weight: 700;
	}

	.access-control-multi-face-group{
		flex: 0 1 170px;
	}

	.access-control-multi-face-filter{
		flex: 1 1 220px;
	}

	.access-control-multi-face-states{
		margin-bottom: 4px;
	}

	.access-control-multi-face-list{
		flex: 1 1 auto;
		min-height: 0;
		overflow-y: auto;
		padding-right: 2px;
	}

	.access-control-multi-face-section{
		border-radius: 10px;
	}

	.access-control-multi-face-section-header{
		align-items: center;
		border-bottom: thin solid currentColor;
		display: flex;
		justify-content: space-between;
		padding: 7px 8px;
	}

	.access-control-multi-face-grid{
		display: grid;
		gap: 6px;
		grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
		padding: 8px;
	}

	.access-control-multi-face-person-card{
		align-items: center;
		border-radius: 10px;
		display: grid;
		gap: 8px;
		grid-template-columns: auto minmax(0, 1fr) auto;
		padding: 8px;
		position: relative;
	}

	.access-control-multi-face-person-card--selectable{
		padding-top: 26px;
	}

	.access-control-multi-face-person-card--selected{
		border-color: var(--v-primary-base) !important;
	}

	.access-control-multi-face-person-card--clickable{
		cursor: pointer;
	}

	.access-control-multi-face-select-toggle{
		position: absolute;
		right: 4px;
		top: 4px;
	}

	.access-control-multi-face-photo-wrap{
		align-items: center;
		display: flex;
		justify-content: center;
	}

	.access-control-multi-face-photo{
		border-radius: 8px;
		height: 42px;
		object-fit: cover;
		width: 42px;
	}

	.access-control-multi-face-person-name{
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.access-control-multi-face-terminals{
		display: flex;
		flex-wrap: wrap;
		gap: 2px;
	}

	.access-control-multi-face-terminal-chip{
		max-width: 100%;
	}

	.access-control-multi-face-status{
		text-align: right;
	}

	.access-control-multi-face-empty{
		align-items: center;
		display: flex;
		flex: 1 1 auto;
		flex-direction: column;
		justify-content: center;
		padding: 18px;
		text-align: center;
	}

	.access-control-multi-face-import-header{
		align-items: center;
		gap: 8px;
	}

	.access-control-multi-face-import-title{
		font-size: 0.95rem;
		font-weight: 700;
	}

	.access-control-multi-face-source-list{
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.access-control-multi-face-source-row{
		border: thin solid currentColor;
		border-radius: 8px;
		padding: 8px;
	}

	.access-control-multi-face-detail-header{
		align-items: center;
		gap: 8px;
	}

	.access-control-multi-face-detail-title{
		font-size: 1rem;
		font-weight: 700;
	}

	.access-control-multi-face-detail-layout{
		display: grid;
		gap: 16px;
		grid-template-columns: 170px minmax(0, 1fr);
	}

	.access-control-multi-face-detail-photo-wrap{
		align-items: center;
		display: flex;
		justify-content: center;
	}

	.access-control-multi-face-detail-photo{
		border-radius: 12px;
		height: 150px;
		object-fit: cover;
		width: 150px;
	}

	.access-control-multi-face-detail-row{
		font-size: 0.86rem;
		margin-bottom: 4px;
	}

	.access-control-multi-face-detail-terminals{
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.access-control-multi-face-terminal-action-row,
	.access-control-multi-face-detail-actions{
		align-items: center;
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
	}

	.access-control-multi-face-move-select{
		max-width: 260px;
		min-width: 180px;
	}

	.access-control-multi-face-events-header{
		align-items: center;
		display: flex;
		gap: 8px;
		justify-content: space-between;
	}

	.access-control-multi-face-chart-wrap{
		border: thin solid currentColor;
		border-radius: 10px;
		min-height: 230px;
		padding: 8px;
	}

	.access-control-multi-face-events-chart{
		height: 210px !important;
		width: 100% !important;
	}

	.access-control-multi-face-events-empty{
		align-items: center;
		display: flex;
		flex-direction: column;
		justify-content: center;
		min-height: 210px;
		text-align: center;
	}

	@media (max-width: 640px){
		.access-control-multi-face-detail-layout{
			grid-template-columns: 1fr;
		}

		.access-control-multi-face-person-card{
			grid-template-columns: auto minmax(0, 1fr);
		}

		.access-control-multi-face-status{
			grid-column: 1 / -1;
			text-align: left;
		}
	}
</style>
